import genAI from '../lib/geminiClient';

/**
 * Handles common Gemini API errors with user-friendly messages.
 * @param {Error} error - The error object from the API.
 * @returns {Object} Error information with message and isInternal flag.
 */
function handleGeminiError(error) {
  if (error?.status === 401 || error?.message?.toLowerCase()?.includes("api key")) {
    return { isInternal: true, message: error?.message };
  }

  if (error?.status === 403 || error?.message?.toLowerCase()?.includes("forbidden")) {
    return { isInternal: true, message: error?.message };
  }

  if (error?.status === 404 || error?.message?.toLowerCase()?.includes("not found")) {
    return { isInternal: true, message: error?.message };
  }

  if (error?.status === 429 || error?.message?.toLowerCase()?.includes("rate limit exceeded")) {
    return { isInternal: true, message: error?.message };
  }

  if (error?.status >= 500) {
    return { isInternal: true, message: error?.message };
  }

  return {
    isInternal: false,
    message: error?.message
  };
}

/**
 * Service for Gemini AI prompt enhancement
 */
export const geminiService = {
  /**
   * Enhances a filled prompt template using Gemini AI
   * @param {string} filledPrompt - The prompt with template placeholders filled with user data
   * @returns {Promise<string>} The enhanced prompt from Gemini
   */
  async enhancePrompt(filledPrompt) {
    try {
      if (!filledPrompt?.trim()) {
        throw new Error('Please provide a prompt to enhance');
      }

      const model = genAI?.getGenerativeModel({ model: 'gemini-2.5-flash' });
      
      // Content safety guidelines to be included in every request
      const safetyGuidelines = `
IMPORTANT CONTENT SAFETY GUIDELINES:
- Avoid words like: intimate, sultry, alluring, provocative, or overly detailed descriptions of body parts
- Do not include: Key political figures (presidents, world leaders, candidates), celebrities (actors, musicians, public icons), or private individuals
- Avoid graphic content: blood, decapitation, torture, or graphic depictions of injury`;
      
      // Exact instruction as specified by user - always act as expert prompt writer
      const instruction = `Act as an expert Prompt Writer. Rewrite, enrich, and optimize that prompt with deeper context, creativity, and clarity — without changing intent.${safetyGuidelines}`;
      
      // Combine the filled prompt with the instruction
      const fullPrompt = `${filledPrompt}\n\n${instruction}`;
      
      const result = await model?.generateContent(fullPrompt);
      const response = await result?.response;
      const rawText = response?.text();
      
      // Extract only the first clean prompt (remove any extra text, options, or noise)
      const cleanedPrompt = this.extractFirstPrompt(rawText);
      
      return cleanedPrompt;
    } catch (error) {
      const errorInfo = handleGeminiError(error);
      if (errorInfo?.isInternal) {
        console.log('Error in prompt enhancement:', errorInfo?.message);
      } else {
        console.error('Error in prompt enhancement:', error);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Extracts the first clean prompt from Gemini's response
   * Removes any introductory text, multiple options, or extra commentary
   * @param {string} text - Raw text from Gemini
   * @returns {string} The first clean prompt only
   */
  extractFirstPrompt(text) {
    if (!text) return '';
    
    let cleaned = text?.trim();
    
    // PRIORITY 1: Extract text within double quotes if present
    // This handles cases where Gemini wraps the actual prompt in quotes
    const quotedMatch = cleaned?.match(/"([^"]+)"/s);
    if (quotedMatch) {
      cleaned = quotedMatch?.[1]?.trim();
      // Remove ** markdown formatting
      cleaned = cleaned?.replace(/\*\*/g, '');
      return cleaned;
    }
    
    // PRIORITY 2: Aggressively remove ALL pretext before the actual prompt
    // Remove everything up to and including separator lines (---)
    cleaned = cleaned?.replace(/^[\s\S]*?---+\s*/m, '');
    
    // Remove any remaining introductory sentences that contain common patterns
    // This catches: "Here's an optimized and enriched prompt, crafted to..."
    const pretextPatterns = [
      /^[^\n]*?(?:Here's|Here is|Here are)[^\n]*?(?:optimized|enhanced|enriched|rewritten|improved)[^\n]*?(?:prompt|version)[^\n]*?[:.][\s\n]*/gi,
      /^[^\n]*?(?:Optimized|Enhanced|Enriched|Rewritten|Improved)\s+(?:Prompt|Version)[^\n]*?[:.][\s\n]*/gi,
      /^[^\n]*?(?:crafted|designed|created)[^\n]*?(?:to evoke|to maintain|to provide)[^\n]*?[:.][\s\n]*/gi,
      /^Option \d+[:.][\s\n]*/gi,
      /^Version \d+[:.][\s\n]*/gi,
      /^Alternative \d+[:.][\s\n]*/gi
    ];
    
    for (const pattern of pretextPatterns) {
      cleaned = cleaned?.replace(pattern, '');
    }
    
    // Remove any remaining separator lines at the start
    cleaned = cleaned?.replace(/^---+\s*/m, '');
    
    // Remove surrounding double quotes if still present
    cleaned = cleaned?.replace(/^"([\s\S]*)"$/s, '$1');
    
    // Remove ** markdown formatting
    cleaned = cleaned?.replace(/\*\*/g, '');
    
    // If there are multiple options (Option 1:, Option 2:, etc.), take only the first
    const optionMatch = cleaned?.match(/^([\s\S]*?)(?=\n\s*Option \d+:|\n\s*Alternative \d+:|\n\s*Version \d+:|$)/i);
    if (optionMatch) {
      cleaned = optionMatch?.[1]?.trim();
    }
    
    // Remove any trailing explanations or notes (usually after double newlines)
    const parts = cleaned?.split(/\n\n+/);
    if (parts?.length > 1) {
      // Check if the last part looks like an explanation rather than part of the prompt
      const lastPart = parts?.[parts?.length - 1]?.toLowerCase();
      if (lastPart?.includes('this prompt') || lastPart?.includes('note:') || lastPart?.includes('explanation:') ||lastPart?.includes('i\'ve ') ||
          lastPart?.includes('the above')) {
        // Remove the explanation part
        cleaned = parts?.slice(0, -1)?.join('\n\n');
      }
    }
    
    return cleaned?.trim();
  },

  /**
   * Enhances a prompt with streaming for real-time feedback
   * @param {string} filledPrompt - The prompt with template placeholders filled
   * @param {Function} onChunk - Callback to handle each streamed chunk
   */
  async enhancePromptStream(filledPrompt, onChunk) {
    try {
      if (!filledPrompt?.trim()) {
        throw new Error('Please provide a prompt to enhance');
      }

      const model = genAI?.getGenerativeModel({ model: 'gemini-2.5-flash' });
      
      // Content safety guidelines to be included in every request
      const safetyGuidelines = `
IMPORTANT CONTENT SAFETY GUIDELINES:
- Avoid words like: intimate, sultry, alluring, provocative, or overly detailed descriptions of body parts
- Do not include: Key political figures (presidents, world leaders, candidates), celebrities (actors, musicians, public icons), or private individuals
- Avoid graphic content: blood, decapitation, torture, or graphic depictions of injury`;
      
      // Exact instruction as specified by user
      const instruction = `Rewrite, enrich, and optimize that prompt with deeper context, creativity, and clarity — without changing intent.${safetyGuidelines}`;
      
      // Combine the filled prompt with the instruction
      const fullPrompt = `${filledPrompt}\n\n${instruction}`;
      
      const result = await model?.generateContentStream(fullPrompt);

      for await (const chunk of result?.stream) {
        const text = chunk?.text();
        if (text) {
          onChunk(text);
        }
      }
    } catch (error) {
      const errorInfo = handleGeminiError(error);
      if (errorInfo?.isInternal) {
        console.log('Error in streaming prompt enhancement:', errorInfo?.message);
      } else {
        console.error('Error in streaming prompt enhancement:', error);
      }
      throw new Error(errorInfo.message);
    }
  }
};